import * as React from 'react';
import { AxiosInstance } from 'axios';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import { ConfirmDialog } from './ui';
import { TRUE_COLORS } from '../theme';

export type ProductMediaItem = { id: number; kind: 'image' | 'video'; url: string; alt: string; sort_order: number };

export const MEDIA_ACCEPT = 'image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime';
const MAX_MB = { image: 10, video: 60 };
const MAX_PER_PRODUCT = 12;

const kindOf = (file: File): 'image' | 'video' | null =>
  /^image\/(jpeg|png|webp)$/.test(file.type) ? 'image' : /^video\/(mp4|webm|quicktime)$/.test(file.type) ? 'video' : null;

/** Why a file can't be uploaded, checked before sending it (the server checks again). */
export const mediaFileProblem = (file: File) => {
  const kind = kindOf(file);
  if (!kind) return `${file.name}: usa JPG, PNG o WebP para fotos y MP4, WebM o MOV para videos.`;
  if (file.size > MAX_MB[kind] * 1024 * 1024) return `${file.name}: pesa más de ${MAX_MB[kind]} MB.`;
  return null;
};

const apiError = (err: any, fallback: string) => {
  const detail = err?.response?.data?.detail ?? err?.message;
  return typeof detail === 'string' && detail ? detail : fallback;
};

/** A form POST with upload progress (fetch can't report it). */
const postForm = (url: string, body: FormData, onProgress?: (fraction: number) => void) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.upload.onprogress = e => { if (e.lengthComputable) onProgress?.(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('No se pudo subir el archivo. Intenta de nuevo.')));
    xhr.onerror = () => reject(new Error('Sin conexión al subir el archivo. Intenta de nuevo.'));
    xhr.send(body);
  });

/**
 * Uploads one photo or video of a product. In production the file goes straight to storage with a
 * signed form and the API only records it; in development it is posted to the API.
 */
export const uploadProductMedia = async (
  axios: AxiosInstance, productId: number | string, file: File, onProgress?: (fraction: number) => void,
): Promise<ProductMediaItem> => {
  const { data: ticket } = await axios.post(`api/products/${productId}/media/upload/`, { content_type: file.type, size: file.size });
  if (ticket.mode === 's3') {
    const form = new FormData();
    Object.entries(ticket.fields as Record<string, string>).forEach(([key, value]) => form.append(key, value));
    form.append('file', file); // S3 requires the file to be the last field
    await postForm(ticket.url, form, onProgress);
    const { data } = await axios.post(`api/products/${productId}/media/`, { key: ticket.key });
    return data;
  }
  const form = new FormData();
  form.append('file', file);
  const { data } = await axios.post(`api/products/${productId}/media/`, form, {
    onUploadProgress: e => { if (e.total) onProgress?.(e.loaded / e.total); },
  });
  return data;
};

const TILE = { width: 132, height: 132, borderRadius: 2, overflow: 'hidden', position: 'relative', flexShrink: 0, bgcolor: TRUE_COLORS.lightBg } as const;

const MediaPreview = ({ kind, url, alt }: { kind: 'image' | 'video'; url: string; alt: string }) =>
  kind === 'image'
    ? <Box component="img" src={url} alt={alt} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    : (
      <>
        <Box component="video" src={url} muted playsInline preload="metadata" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        <PlayCircleOutlineIcon sx={{ position: 'absolute', left: 8, bottom: 8, color: '#fff', filter: 'drop-shadow(0 1px 2px rgba(0,0,0,.5))' }} />
      </>
    );

const AddTile = ({ onFiles, disabled }: { onFiles: (files: File[]) => void; disabled?: boolean }) => {
  const input = React.useRef<HTMLInputElement>(null);
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={() => input.current?.click()}
      sx={{
        ...TILE, border: `1.5px dashed ${TRUE_COLORS.borderSolid}`, cursor: disabled ? 'default' : 'pointer', color: 'text.secondary',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, font: 'inherit',
        '&:hover': disabled ? undefined : { borderColor: 'text.secondary' },
      }}
    >
      <AddPhotoAlternateOutlinedIcon />
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 500 }}>Agregar fotos o videos</Typography>
      <input
        ref={input}
        hidden
        type="file"
        multiple
        accept={MEDIA_ACCEPT}
        onChange={e => { onFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
      />
    </Box>
  );
};

const HINT = 'JPG, PNG o WebP hasta 10 MB; MP4, WebM o MOV hasta 60 MB. La primera es la principal en tu catálogo en línea.';

/** Photos and videos of a saved product: upload, reorder and delete. Changes are saved right away. */
export const ProductMediaManager = ({ axios, productId }: { axios: AxiosInstance; productId: number | string }) => {
  const [media, setMedia] = React.useState<ProductMediaItem[] | null>(null);
  const [uploads, setUploads] = React.useState<{ key: string; name: string; progress: number }[]>([]);
  const [error, setError] = React.useState('');
  const [removing, setRemoving] = React.useState<ProductMediaItem | null>(null);

  React.useEffect(() => {
    axios.get(`api/products/${productId}/media/`).then(r => setMedia(r.data ?? [])).catch(() => setMedia([]));
  }, [axios, productId]);

  const addFiles = async (files: File[]) => {
    setError('');
    const room = MAX_PER_PRODUCT - (media?.length ?? 0) - uploads.length;
    const problems = files.map(mediaFileProblem).filter(Boolean) as string[];
    const valid = files.filter(f => !mediaFileProblem(f)).slice(0, Math.max(room, 0));
    if (files.filter(f => !mediaFileProblem(f)).length > valid.length) problems.push(`Máximo ${MAX_PER_PRODUCT} fotos y videos por producto.`);
    if (problems.length) setError(problems.join(' '));
    // One at a time keeps the order the files were chosen in.
    for (const file of valid) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      setUploads(u => [...u, { key, name: file.name, progress: 0 }]);
      try {
        const item = await uploadProductMedia(axios, productId, file, progress => setUploads(u => u.map(x => (x.key === key ? { ...x, progress } : x))));
        setMedia(m => [...(m ?? []), item]);
      } catch (err) {
        setError(apiError(err, `No se pudo subir ${file.name}.`));
      } finally {
        setUploads(u => u.filter(x => x.key !== key));
      }
    }
  };

  const move = async (index: number, delta: number) => {
    if (!media) return;
    const next = [...media];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    const ordered = next.map((m, i) => ({ ...m, sort_order: i + 1 }));
    setMedia(ordered);
    try {
      await Promise.all(ordered.filter((m, i) => media[i]?.id !== m.id)
        .map(m => axios.patch(`api/products/${productId}/media/${m.id}/`, { sort_order: m.sort_order })));
    } catch (err) {
      setError(apiError(err, 'No se pudo cambiar el orden.'));
    }
  };

  const remove = async () => {
    if (!removing) return;
    try {
      await axios.delete(`api/products/${productId}/media/${removing.id}/`);
      setMedia(m => (m ?? []).filter(x => x.id !== removing.id));
    } catch (err) {
      setError(apiError(err, 'No se pudo eliminar.'));
    }
    setRemoving(null);
  };

  return (
    <Stack spacing={1.5}>
      {error && <Alert severity="warning" onClose={() => setError('')}>{error}</Alert>}
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        {media === null && <Box sx={{ ...TILE, display: 'grid', placeItems: 'center' }}><CircularProgress size={22} /></Box>}
        {(media ?? []).map((item, index) => (
          <Box key={item.id} sx={{ ...TILE, '&:hover .media-actions': { opacity: 1 } }}>
            <MediaPreview kind={item.kind} url={item.url} alt={item.alt} />
            {index === 0 && <Chip size="small" label="Principal" sx={{ position: 'absolute', top: 6, left: 6, bgcolor: 'rgba(255,255,255,0.92)', fontWeight: 600, height: 22 }} />}
            <Stack
              className="media-actions"
              direction="row"
              sx={{
                position: 'absolute', right: 4, bottom: 4, gap: 0.25, opacity: { xs: 1, md: 0 }, transition: 'opacity .15s',
                '& .MuiIconButton-root': { bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' } },
              }}
            >
              {index > 0 && <Tooltip title="Mover antes"><IconButton size="small" onClick={() => move(index, -1)}><ChevronLeftIcon fontSize="small" /></IconButton></Tooltip>}
              {index < (media?.length ?? 0) - 1 && <Tooltip title="Mover después"><IconButton size="small" onClick={() => move(index, 1)}><ChevronRightIcon fontSize="small" /></IconButton></Tooltip>}
              <Tooltip title="Eliminar"><IconButton size="small" onClick={() => setRemoving(item)}><DeleteOutlineIcon fontSize="small" sx={{ color: TRUE_COLORS.danger }} /></IconButton></Tooltip>
            </Stack>
          </Box>
        ))}
        {uploads.map(u => (
          <Box key={u.key} sx={{ ...TILE, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, p: 1 }}>
            <CircularProgress size={28} variant={u.progress > 0 ? 'determinate' : 'indeterminate'} value={u.progress * 100} />
            <Typography noWrap sx={{ fontSize: '0.7rem', color: 'text.secondary', maxWidth: '100%' }}>{u.name}</Typography>
          </Box>
        ))}
        {media !== null && media.length + uploads.length < MAX_PER_PRODUCT && <AddTile onFiles={addFiles} />}
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{HINT}</Typography>
      <ConfirmDialog
        open={Boolean(removing)}
        danger
        title={removing?.kind === 'video' ? '¿Eliminar este video?' : '¿Eliminar esta foto?'}
        description="Dejará de mostrarse en tu catálogo en línea."
        confirmLabel="Eliminar"
        onClose={() => setRemoving(null)}
        onConfirm={remove}
      />
    </Stack>
  );
};

/** Photos and videos chosen before the product exists; AddProduct uploads them right after creating it. */
export const ProductMediaPicker = ({ files, onChange }: { files: File[]; onChange: (files: File[]) => void }) => {
  const [error, setError] = React.useState('');
  const previews = React.useMemo(() => files.map(file => ({ file, url: URL.createObjectURL(file) })), [files]);
  React.useEffect(() => () => previews.forEach(p => URL.revokeObjectURL(p.url)), [previews]);

  const add = (chosen: File[]) => {
    const problems = chosen.map(mediaFileProblem).filter(Boolean) as string[];
    const valid = chosen.filter(f => !mediaFileProblem(f));
    const next = [...files, ...valid].slice(0, MAX_PER_PRODUCT);
    if (files.length + valid.length > next.length) problems.push(`Máximo ${MAX_PER_PRODUCT} fotos y videos por producto.`);
    setError(problems.join(' '));
    onChange(next);
  };

  return (
    <Stack spacing={1.5}>
      {error && <Alert severity="warning" onClose={() => setError('')}>{error}</Alert>}
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
        {previews.map(({ file, url }, index) => (
          <Box key={url} sx={TILE}>
            <MediaPreview kind={kindOf(file) ?? 'image'} url={url} alt={file.name} />
            {index === 0 && <Chip size="small" label="Principal" sx={{ position: 'absolute', top: 6, left: 6, bgcolor: 'rgba(255,255,255,0.92)', fontWeight: 600, height: 22 }} />}
            <IconButton
              size="small"
              aria-label="Quitar"
              onClick={() => onChange(files.filter((_, i) => i !== index))}
              sx={{ position: 'absolute', right: 4, top: 4, bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
        {files.length < MAX_PER_PRODUCT && <AddTile onFiles={add} />}
      </Stack>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{HINT}</Typography>
    </Stack>
  );
};
